(() => {
  if (customElements.get('kg-hero-carousel')) return;

  customElements.define('kg-hero-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this._listeners) return;

      this._slides = Array.from(this.querySelectorAll('[data-slide]'));
      this._controls = this.querySelector('[data-controls]');
      this._status = this.querySelector('[data-status]');
      this._dots = Array.from(this.querySelectorAll('[data-dot]'));
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._playing = this._slides.length > 1 && !this._motion.matches;
      this._hovered = this.matches(':hover');
      this._focused = this.contains(document.activeElement);
      this._index = Math.max(0, this._slides.findIndex((slide) => !slide.hidden));

      const interval = Number(this.dataset.autoplayInterval);
      this._interval = Number.isFinite(interval) && interval > 0 ? interval : 6000;
      this._listeners = new AbortController();
      const options = { signal: this._listeners.signal };

      if (this._controls) {
        this._controls.hidden = this._slides.length < 2;
        this._controls.addEventListener('click', (event) => {
          const button = event.target.closest('[data-prev], [data-next], [data-dot]');
          if (!button || !this._controls.contains(button)) return;

          let index = this._index;
          if (button.hasAttribute('data-prev')) index -= 1;
          else if (button.hasAttribute('data-next')) index += 1;
          else {
            index = Number(button.dataset.index);
            if (!Number.isInteger(index) || index < 0 || index >= this._slides.length) return;
          }
          this._navigate(index);
        }, options);

        this._controls.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (event.target.closest('input, select, textarea, [contenteditable="true"]')) return;
          event.preventDefault();
          this._navigate(this._index + (event.key === 'ArrowLeft' ? -1 : 1));
        }, options);
      }

      this.addEventListener('mouseenter', () => {
        this._hovered = true;
        this._schedule();
      }, options);
      this.addEventListener('mouseleave', () => {
        this._hovered = false;
        this._schedule();
      }, options);
      this.addEventListener('focusin', () => {
        this._focused = true;
        this._schedule();
      }, options);
      this.addEventListener('focusout', (event) => {
        this._focused = this.contains(event.relatedTarget);
        this._schedule();
      }, options);
      document.addEventListener('visibilitychange', () => this._schedule(), options);
      this._motion.addEventListener('change', (event) => {
        if (event.matches) this._playing = false;
        this._syncPlayback();
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        const targetSlide = event.target.closest?.('[data-slide]');
        let index = this._slides.indexOf(targetSlide);
        const blockId = event.detail?.blockId;
        if (index < 0 && blockId) {
          index = this._slides.findIndex((slide) =>
            slide.dataset.slideId === blockId || slide.dataset.blockId === blockId ||
            slide.id === blockId || slide.id === `shopify-block-${blockId}`
          );
        }
        if (index >= 0) this._navigate(index);
      }, options);

      this._show(this._index);
      this._syncPlayback();
    }

    disconnectedCallback() {
      clearTimeout(this._timer);
      this._listeners?.abort();
      this._listeners = null;
    }

    _navigate(index) {
      this._playing = false;
      this._syncPlayback();
      this._show(index);
    }

    _show(index) {
      if (!this._slides.length) return;
      this._index = (index + this._slides.length) % this._slides.length;
      this._slides.forEach((slide, position) => { slide.hidden = position !== this._index; });
      this._dots.forEach((dot) => {
        dot.setAttribute('aria-pressed', String(Number(dot.dataset.index) === this._index));
      });
      if (this._status) {
        this._status.textContent = (this.dataset.statusTemplate || '[current] of [total]')
          .replace('[current]', String(this._index + 1))
          .replace('[total]', String(this._slides.length));
      }
      if (this._slides.length > 1) {
        const currentImage = this._slides[this._index].querySelector('img');
        const nextImage = this._slides[(this._index + 1) % this._slides.length].querySelector('img');
        if (nextImage) {
          const preloadNext = () => { nextImage.loading = 'eager'; };
          if (!currentImage || currentImage.complete) preloadNext();
          else currentImage.addEventListener('load', preloadNext, {
            once: true,
            signal: this._listeners.signal
          });
        }
      }
    }

    _syncPlayback() {
      if (this._status) {
        this._status.setAttribute('aria-live', this._playing ? 'off' : 'polite');
        this._status.setAttribute('aria-atomic', 'true');
      }
      this._schedule();
    }

    _schedule() {
      clearTimeout(this._timer);
      if (!this.isConnected || !this._playing || this._slides.length < 2 ||
          this._hovered || this._focused || document.hidden) return;
      this._timer = setTimeout(() => {
        this._show(this._index + 1);
        this._schedule();
      }, this._interval);
    }
  });
})();
